import type { Api } from "../../data";

/**
 * Media-Kit und Nachprovision im Creator-Portal (0074). Lesen nur über Lesefunktionen (talent_kit_self, talent_post_term_info),
 * nie direkt aus den Tabellen: Preise und Adressen der Marken bleiben bei der Agentur.
 */

export interface KitChannel {
  platform: string;
  handle?: string | null;
  followers?: number | null;
  avg_views?: number | null;
  engagement_bp?: number | null;
  as_of?: string | null;
}
export interface KitLinkInfo {
  kind: "single" | "pitch";
  title: string | null;
  brand: string | null;
  expires_at: string;
  created_at: string;
  show_rates: boolean;
  show_references: boolean;
  show_instagram: boolean;
  /** weitere Creator in derselben Liste */
  others: number;
  views: number;
  last_view: string | null;
}
export interface KitSelf {
  bio_de: string | null;
  bio_en: string | null;
  topics: string[];
  channels: KitChannel[];
  rates_count: number;
  proposal: { bio_de: string | null; bio_en: string | null; topics: string[] | null; at: string } | null;
  decision: { decision: "accepted" | "rejected"; at: string; note: string | null } | null;
  instagram_shared: boolean;
  instagram_blocks: string[];
  references: { brand: string | null; year: number | null; note: string | null }[];
  has_image: boolean;
  links: KitLinkInfo[];
}

const parse = <T>(r: T | string): T => (typeof r === "string" ? (JSON.parse(r) as T) : r);

export async function loadKitSelf(api: Pick<Api, "read">, talentId: string): Promise<KitSelf> {
  return parse(await api.read<KitSelf | string>("talent_kit_self", { p_talent: talentId }));
}

/** Vorschlag an die Agentur (Glocke dort); sichtbar wird er erst nach der Freigabe. */
export async function proposeKit(api: Pick<Api, "rpc">, talentId: string, v: { bioDe: string; bioEn: string; topics: string[] }): Promise<true> {
  await api.rpc("talent_kit_propose", { p_talent: talentId, p_bio_de: v.bioDe.trim() || null, p_bio_en: v.bioEn.trim() || null, p_topics: v.topics });
  return true;
}

/** Themen aus einem Textfeld (Komma oder Zeilenumbruch), ohne Dopplungen, höchstens 12. */
export function themenAusText(text: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of text.split(/[,\n;]/)) {
    const t = raw.trim().replace(/\s+/g, " ");
    if (!t || seen.has(t.toLowerCase())) continue;
    seen.add(t.toLowerCase());
    out.push(t);
  }
  return out;
}

export interface PostTermInfo {
  talent_id: string;
  org_name: string;
  from: string;
  until: string;
  months: number;
  bp: number | null;
  steps: { upto: number; bp: number }[] | null;
  scope: "concluded" | "initiated";
  /** Satz umgestellter laufender Kooperationen nach dem Ende (0 = keine Provision der Agentur) */
  after_bp?: number;
  /** monthly: laufende Kooperation, Satz je Abrechnungsmonat nach der Staffel (commission_bp = laufender Monat) */
  deals: { deal_id?: string; title: string; brand: string | null; commission_bp: number | null; status: string; monthly?: boolean }[];
}

export async function loadPostTermInfo(api: Pick<Api, "read">): Promise<PostTermInfo[]> {
  return parse(await api.read<PostTermInfo[] | string>("talent_post_term_info", {})) ?? [];
}
